import { STORAGE_NAMESPACE, TUNABLES } from "../config";
import { type NamesAnswer, namesUrlOf, parseNamesAnswer } from "../net/names";

/**
 * NM2, names owned by accounts, the page's half: a signed-in visitor's name is claimed for the
 * account at the Worker's `/names` (`server/names.ts`), and a guest's is checked free, so the
 * front page never offers a name another account holds. The room checks again at every join
 * (`roomObject.ts`), so a page that skips this plays under a stand-in. Pure but for `post`.
 */

/** The signed-in page's session (`server/session.ts`), kept until Sign out. */
export const SESSION_KEY = `${STORAGE_NAMESPACE}:session`;
/** The name the account holds, as the Worker last said: it follows the account to this device. */
export const HELD_NAME_KEY = `${STORAGE_NAMESPACE}:heldName`;

/** How a claim ended, as the name field shows it. */
export type ClaimOutcome =
  | { readonly result: "held"; readonly name: string }
  | { readonly result: "taken" | "reserved" | "session" | "off" | "unreachable" };

/** The Worker's names desk as the page asks it. */
export interface NameDesk {
  /** Claims `name` for the signed-in account. */
  claim(name: string): Promise<ClaimOutcome>;
  /** Whether `name` is free; undefined when the Worker did not answer. */
  isFree(name: string): Promise<boolean | undefined>;
}

/** A claim's answer as the field shows it; an answer that is not the Worker's is "unreachable". */
export function claimOutcomeOf(answer: NamesAnswer | undefined): ClaimOutcome {
  if (answer === undefined || "free" in answer) {
    return { result: "unreachable" };
  }
  if ("held" in answer) {
    return { result: "held", name: answer.held };
  }
  switch (answer.refused) {
    case "taken":
    case "reserved":
    case "session":
    case "off":
      return { result: answer.refused };
    default:
      return { result: "unreachable" };
  }
}

/**
 * The desk at `serverUrl`: `post` sends a body to a URL and resolves to the answer's text whatever
 * its status (a failure throws). A session of undefined claims nothing: the visitor is a guest.
 */
export function nameDeskOf(options: {
  readonly serverUrl: string;
  readonly session: string | undefined;
  readonly post: (url: string, body: string) => Promise<string>;
}): NameDesk {
  const url = namesUrlOf(options.serverUrl);
  const ask = async (body: object): Promise<NamesAnswer | undefined> => {
    try {
      return parseNamesAnswer(await options.post(url, JSON.stringify(body)));
    } catch {
      return undefined;
    }
  };
  return {
    async claim(name) {
      if (options.session === undefined) {
        return { result: "session" };
      }
      return claimOutcomeOf(await ask({ kind: "claim", session: options.session, name }));
    },
    async isFree(name) {
      const answer = await ask({ kind: "check", name });
      return answer !== undefined && "free" in answer ? answer.free : undefined;
    },
  };
}

/**
 * A free name from `first` on: `first` itself unless another account holds it, else the dice's
 * next roll, `rollTries` at most; a Worker that does not answer lets the name stand.
 */
export async function freeNameFrom(
  first: string,
  roll: () => string,
  desk: NameDesk,
): Promise<string> {
  let name = first;
  for (let tries = 0; tries < TUNABLES.names.rollTries; tries += 1) {
    if ((await desk.isFree(name)) !== false) {
      return name;
    }
    name = roll();
  }
  return name;
}

/**
 * A claimed name from `first` on: `first` unless another account holds it or a room's stand-in
 * has its shape, else the dice's next roll, `rollTries` at most. Any other outcome ends the
 * search as it is.
 */
export async function heldNameFrom(
  first: string,
  roll: () => string,
  desk: NameDesk,
): Promise<ClaimOutcome> {
  let outcome = await desk.claim(first);
  for (let tries = 1; tries < TUNABLES.names.rollTries; tries += 1) {
    if (outcome.result !== "taken" && outcome.result !== "reserved") {
      return outcome;
    }
    outcome = await desk.claim(roll());
  }
  return outcome;
}
